// Configuração consciente de ambiente para o frontend LLM
// Observação: todos os consumidores devem chamar EnvironmentConfig.init() no bootstrap
// antes de acessar APIs, garantindo que overrides e env.local.json sejam aplicados.

const ENV_LOCAL_URL = "/config/env.local.json";

// Declaração de tipos globais para o window
declare global {
  interface Window {
    __LLM_ENV__?: {
      apiBaseUrl?: string;
    };
  }
}

export type EnvironmentName = "local" | "production" | "unsupported" | "unknown";

export interface EnvLocalConfig {
  apiBaseUrl?: string;
  catalogPreviewUrl?: string;
}

/**
 * Nomeia o ambiente atual com base em hostname e porta.
 */
function getEnvironmentName(): EnvironmentName {
  if (typeof window === "undefined") {
    return "unknown";
  }

  const { hostname, protocol } = window.location;

  const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";

  if (isLocalHost) {
    return "local";
  }

  const prodDomains = ["rodrigonahum.com.br", "www.rodrigonahum.com.br", "lmdocs.com", "www.lmdocs.com"];
  if (prodDomains.includes(hostname)) {
    return "production";
  }

  // The dedicated staging site was retired. Any preview-like host must fail closed
  // instead of transparently reaching a non-production backend.
  if (hostname.includes("pages.dev") || hostname.includes("preview") || hostname.includes("staging") || hostname.includes("homolog")) {
    return "unsupported";
  }

  if (protocol === "file:") {
    return "local";
  }

  return "unknown";
}

/**
 * Define a base URL padrão de API com base no ambiente detectado.
 */
function getDefaultApiBaseUrl(): string {
  const envName = getEnvironmentName();

  switch (envName) {
    case "local":
      // No fluxo de desenvolvimento atual, o frontend local (8788) consome os workers remotos de dev.
      return "https://settings-sync-dev.rnahumaf.workers.dev";
    case "production":
      return window.location.origin; // Usa o domínio atual (lmdocs ou rodrigonahum)
    case "unsupported":
    case "unknown":
    default:
      return typeof window !== "undefined" ? window.location.origin : "";
  }
}

// Cache interno
let resolvedApiBaseUrl: string | null = null;
let envLocalConfig: EnvLocalConfig | null = null;
let envConfigLoaded = false;
let initPromise: Promise<string> | null = null;

/**
 * Tenta carregar config/env.local.json uma única vez.
 */
async function loadEnvConfig(): Promise<EnvLocalConfig | null> {
  if (envConfigLoaded) {
    return envLocalConfig;
  }

  envConfigLoaded = true;

  // Em produção/same-origin não buscamos env.local.json.
  // O arquivo só é usado no fluxo de desenvolvimento local.
  if (getEnvironmentName() !== "local") {
    envLocalConfig = null;
    return envLocalConfig;
  }

  if (typeof window === "undefined" || typeof fetch === "undefined") {
    envLocalConfig = null;
    return envLocalConfig;
  }

  try {
    const response = await fetch(ENV_LOCAL_URL, {
      method: "GET",
      credentials: "same-origin",
    });

    if (!response.ok) {
      envLocalConfig = null;
      return envLocalConfig;
    }

    const json = await response.json();
    if (json && typeof json === "object") {
      envLocalConfig = {
        apiBaseUrl: typeof json.apiBaseUrl === "string" && json.apiBaseUrl.trim() ? json.apiBaseUrl.trim() : undefined,
        catalogPreviewUrl: typeof json.catalogPreviewUrl === 'string' ? json.catalogPreviewUrl.trim() : undefined,
      };
    } else {
      envLocalConfig = null;
    }
  } catch (error) {
    envLocalConfig = null;
  }

  return envLocalConfig;
}

/**
 * Aplica a estratégia de resolução da API base.
 */
async function resolveApiBaseUrl(): Promise<string> {
  if (resolvedApiBaseUrl) {
    return resolvedApiBaseUrl;
  }

  // 1) Override global em window.__LLM_ENV__
  if (typeof window !== "undefined" && window.__LLM_ENV__) {
    const overrideBase = typeof window.__LLM_ENV__.apiBaseUrl === "string" ? window.__LLM_ENV__.apiBaseUrl.trim() : "";
    if (overrideBase) {
      resolvedApiBaseUrl = overrideBase;
      return resolvedApiBaseUrl;
    }
  }

  // 2) env.local.json (somente no ambiente local)
  if (getEnvironmentName() === "local") {
    const localConfig = await loadEnvConfig();
    if (localConfig && typeof localConfig.apiBaseUrl === "string") {
      const localBase = localConfig.apiBaseUrl.trim();
      if (localBase) {
        resolvedApiBaseUrl = localBase;
        return resolvedApiBaseUrl;
      }
    }
  }

  // 3) Fallback padrão
  resolvedApiBaseUrl = getDefaultApiBaseUrl();
  return resolvedApiBaseUrl;
}

async function getApiBaseUrl(): Promise<string> {
  return resolveApiBaseUrl();
}

function getCachedApiBaseUrl(): string {
  if (!resolvedApiBaseUrl) {
    return getDefaultApiBaseUrl();
  }
  return resolvedApiBaseUrl;
}

async function initEnvironmentConfig(): Promise<string> {
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    return await resolveApiBaseUrl();
  })();

  return initPromise;
}

function getCatalogPreviewUrl(): string | null {
  if (getEnvironmentName() !== 'local' || !envLocalConfig?.catalogPreviewUrl) return null;
  const url = new URL(envLocalConfig.catalogPreviewUrl);
  if (url.protocol !== 'http:' || !['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)
    || url.pathname !== '/api/models-sot' || url.username || url.password || url.search || url.hash) {
    throw new Error('A prévia do catálogo requer um endpoint público no loopback local.');
  }
  return url.toString();
}

export const EnvironmentConfig = {
  init: initEnvironmentConfig,
  getApiBaseUrl,
  getCachedApiBaseUrl,
  getEnvironmentName,
  getCatalogPreviewUrl,
  isCatalogPreview: () => getCatalogPreviewUrl() !== null,
};

export { getEnvironmentName, getApiBaseUrl, getCachedApiBaseUrl };
